/* eslint-disable react/iframe-missing-sandbox */

import React, { forwardRef, useCallback, useEffect } from 'react';
import { View } from 'react-native';
import type WebView from 'react-native-webview';

import type { HostedFrameProps } from './HostedFrame.types';

/**
 * Wrapper around `iframe` to connect with React in a similar way to WebView.
 */
export const HostedFrame = forwardRef((props: HostedFrameProps, _ref) => {
  const {
    frameId,
    frameHeight,
    source,
    containerStyle,
    isLoading,
    title,
    onMessage,
  } = props;

  useMessageListener(onMessage);

  const frameStyle = isLoading
    ? frameLoadingStyle
    : { ...baseStyle, height: frameHeight };

  return (
    <View testID={frameId} style={containerStyle}>
      <iframe
        id={frameId}
        style={frameStyle}
        title={title}
        srcDoc={source.html}
        sandbox="allow-same-origin allow-scripts allow-forms"
        src="about:blank"
      />
    </View>
  );
});

const useMessageListener = (
  onMessage?: React.ComponentProps<typeof WebView>['onMessage'],
) => {
  const processMessage = useCallback(
    (message: MessageEvent) => {
      if (!message.data.includes?.('fromHostedFrame')) return;

      onMessage?.({ nativeEvent: { data: message.data } } as never);
    },
    [onMessage],
  );

  useEffect(() => {
    window.addEventListener('message', processMessage, false);

    return () => {
      window.removeEventListener('message', processMessage, false);
    };
  }, [processMessage]);
};

// ─── Styles ────────────────────────────────────────────────────────────
// For web views to function properly on Android, their container must be at least 1x1 in size.

const baseStyle = { border: 0 };
const frameLoadingStyle = { ...baseStyle, height: 1 };
